import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../../partials/Sidebar';
import Header from '../../partials/Header';
import { Box, TextField, IconButton, Paper, Typography } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import ChatIcon from '@mui/icons-material/Chat';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import AttackChainKillChain from '../../components/offensive/AttackChainKillChain';
import TechniquePanel from '../../components/offensive/TechniquePanel';
import TerminalOutput from '../../components/offensive/TerminalOutput';
import EngagementControl from '../../components/offensive/EngagementControl';
import EngagementManager from '../../components/offensive/EngagementManager';
import useOffensiveStore from '../../stores/offensiveStore';
import offensiveApi from '../../services/offensiveApi';


const ChatMessage = ({ message, isUser, isLoading }) => {
  // Parse message content to separate text and code blocks
  const parseContent = (text) => {
    if (!text) return [];
    
    const parts = [];
    const lines = text.split('\n');
    let currentPart = { type: 'text', content: '' };
    let inCodeBlock = false;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      // Check for code block markers
      if (line.trim().startsWith('```')) {
        if (inCodeBlock) {
          // End of code block
          parts.push({ type: 'code', content: currentPart.content });
          currentPart = { type: 'text', content: '' };
          inCodeBlock = false;
        } else {
          // Start of code block
          if (currentPart.content.trim()) {
            parts.push({ type: 'text', content: currentPart.content });
          }
          currentPart = { type: 'code', content: '' };
          inCodeBlock = true;
        }
      } else {
        // Add line to current part
        if (currentPart.content) {
          currentPart.content += '\n' + line;
        } else {
          currentPart.content = line;
        }
      }
    }
    
    // Add the last part
    if (currentPart.content.trim()) {
      parts.push(currentPart);
    }
    
    return parts;
  };

  const renderPart = (part, index) => {
    if (part.type === 'code') {
      return (
        <SyntaxHighlighter
          key={index}
          language="python"
          style={vscDarkPlus}
          customStyle={{
            margin: '8px 0',
            padding: '12px',
            background: 'rgba(0, 0, 0, 0.3)',
            fontSize: '0.85rem',
            borderRadius: '8px',
          }}
        >
          {part.content}
        </SyntaxHighlighter>
      );
    } else {
      return (
        <Typography 
          key={index}
          sx={{ 
            color: '#fff', 
            opacity: 0.9,
            fontSize: '0.9rem',
            whiteSpace: 'pre-wrap',
            marginBottom: part.content.trim() ? '8px' : '0'
          }}
        >
          {part.content}
        </Typography>
      );
    }
  };

  const contentParts = parseContent(message);

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        mb: 2,
      }}
    >
      <Paper
        sx={{
          p: 2,
          maxWidth: '70%',
          bgcolor: isUser ? 'rgba(88, 101, 242, 0.2)' : 'rgba(255, 255, 255, 0.05)',
          borderRadius: 2,
          animation: isLoading ? 'fadeInOut 2s ease-in-out infinite' : 'none',
          '@keyframes fadeInOut': {
            '0%': { opacity: 0.4 },
            '50%': { opacity: 1 },
            '100%': { opacity: 0.4 },
          },
        }}
      >
        {contentParts.length > 0 ? (
          contentParts.map((part, index) => renderPart(part, index))
        ) : (
          <Typography sx={{ 
            color: '#fff', 
            opacity: 0.9,
            fontSize: '0.9rem'
          }}>
            {message}
          </Typography>
        )}
      </Paper>
    </Box>
  );
};

const Offensive = () => {
  const { engagementId } = useParams();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadingDots, setLoadingDots] = useState('');
  const [showEngagementManager, setShowEngagementManager] = useState(false);
  const [expandedPanel, setExpandedPanel] = useState(null); // null = both visible, 'techniques' = techniques full width, 'terminal' = terminal full width
  const [chainCollapsed, setChainCollapsed] = useState(false);
  const [chainHeight, setChainHeight] = useState(190);
  const chainDragRef = useRef(null);
  const messagesEndRef = useRef(null);

  const { mode, currentEngagement, setCurrentEngagement, setActiveFlowId, stopEngagement, activeFlowId, isRunning } = useOffensiveStore();

  const handleChainDragStart = useCallback((e) => {
    e.preventDefault();
    const startY = e.clientY;
    const startH = chainHeight;
    const onMove = (ev) => {
      const newH = Math.min(500, Math.max(120, startH + ev.clientY - startY));
      setChainHeight(newH);
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [chainHeight]);

  // Sync engagement from URL parameter — always trust the URL over persisted state
  useEffect(() => {
    if (engagementId) {
      const urlId = parseInt(engagementId);
      if (!currentEngagement || currentEngagement.id !== urlId) {
        setActiveFlowId(null);
        stopEngagement();
        setCurrentEngagement({ id: urlId });
        offensiveApi.getEngagements().then(list => {
          const eng = list.find(e => e.id === urlId);
          if (eng) {
            setCurrentEngagement(eng);
            // Auto-reconnect to the last active flow if one exists
            try {
              const aiConfig = JSON.parse(eng.offensive_scope?.ai_config || '{}');
              const flowId = aiConfig.pentagi_flow_ids?.[0];
              if (flowId) {
                setActiveFlowId(String(flowId));
              }
            } catch (e) { /* no ai_config */ }
          }
        }).catch(() => {});
      }
    }
  }, [engagementId, currentEngagement, setCurrentEngagement, setActiveFlowId, stopEngagement]);

  // Chat agent adapter — uses the same factory as EngagementControl
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    let interval;
    if (isLoading) {
      let dotCount = 0;
      interval = setInterval(() => {
        setLoadingDots('.'.repeat(dotCount % 4));
        dotCount++;
      }, 500);
    }
    return () => clearInterval(interval);
  }, [isLoading]);

  useEffect(() => {
    if (isLoading) {
      setMessages(prev => {
        const newMessages = [...prev];
        const loadingMessageIndex = newMessages.findIndex(msg => msg.isLoading);
        if (loadingMessageIndex !== -1) {
          newMessages[loadingMessageIndex] = {
            text: `Thinking${loadingDots}`,
            isUser: false,
            isLoading: true
          };
        }
        return newMessages;
      });
    }
  }, [loadingDots, isLoading]);

  const isFlowActive = Boolean(activeFlowId && isRunning);
  const showChatDrawer = chatOpen;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    const userMessage = input.trim();
    setMessages(prev => [...prev, { text: userMessage, isUser: true }]);
    setInput('');
    setIsLoading(true);

    if (!isFlowActive) {
      setIsLoading(false);
      return;
    }

    try {
      await offensiveApi.sendFlowMessage(activeFlowId, userMessage);
      setMessages(prev => [...prev, {
        text: 'Message sent to the running flow. Watch the terminal for the agent\'s response.',
        isUser: false,
      }]);
    } catch (error) {
      console.error('Flow message error:', error);
      setMessages(prev => [...prev, {
        text: `Failed to send message to flow: ${error.message}`,
        isUser: false,
      }]);
    }
    setIsLoading(false);
  };

  const handleNewEngagement = () => {
    setShowEngagementManager(true);
  };

  const handleEngagementSelect = (engagement) => {
    setCurrentEngagement(engagement);
    setShowEngagementManager(false);
  };

  const handleEngagementStart = (engagement) => {
    setCurrentEngagement(engagement);
    setShowEngagementManager(false);
  };

  return (
    <div className="flex h-[100dvh] overflow-hidden">
      {/* Sidebar */}
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

      {/* Content area */}
      <div className="relative flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
        {/*  Site header */}
        <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

        {/* Main content */}
        <Box sx={{ height: 'calc(100vh - 64px)', p: 2, background: '#0f172a' }}>
          {showEngagementManager ? (
            /* Engagement Manager View */
            <EngagementManager 
              onEngagementSelect={handleEngagementSelect}
              onEngagementStart={handleEngagementStart}
            />
          ) : (
            /* Offensive Operations View */
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 2 }}>
              {/* Control Panel */}
              <EngagementControl
                onToggleChat={() => setChatOpen(prev => !prev)}
                chatOpen={chatOpen}
                isFlowActive={isFlowActive}
              />

              {/* Main Content - Redesigned Layout */}
              <Box sx={{ 
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                flex: 1,
                minHeight: 0,
              }}>
                {/* Top - Attack Chain Kill Chain (Full Width) */}
                <Box sx={{
                  height: chainCollapsed ? '36px' : `${chainHeight}px`,
                  minHeight: chainCollapsed ? '36px' : `${chainHeight}px`,
                  overflow: 'hidden',
                  transition: chainCollapsed ? 'height 0.3s ease, min-height 0.3s ease' : 'none',
                  position: 'relative',
                }}>
                  <AttackChainKillChain
                    collapsed={chainCollapsed}
                    onToggleCollapse={() => setChainCollapsed(!chainCollapsed)}
                  />
                  {!chainCollapsed && (
                    <Box
                      onMouseDown={handleChainDragStart}
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: '6px',
                        cursor: 'row-resize',
                        zIndex: 20,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        '&:hover > div, &:active > div': {
                          opacity: 1,
                          background: 'rgba(99, 102, 241, 0.5)',
                        },
                      }}
                    >
                      <Box sx={{
                        width: '40px',
                        height: '3px',
                        borderRadius: '2px',
                        background: 'rgba(99, 102, 241, 0.25)',
                        opacity: 0.6,
                        transition: 'all 0.2s ease',
                      }} />
                    </Box>
                  )}
                </Box>

                {/* Bottom - Techniques & Terminal with Horizontal Toggle */}
                <Box sx={{ 
                  display: 'flex',
                  gap: 0,
                  flex: 1,
                  minHeight: 0,
                  position: 'relative',
                }}>
                  {/* Active Techniques Panel / Interact Chat overlay */}
                  <Box sx={{
                    width: expandedPanel === 'terminal' ? '0%' : (expandedPanel === 'techniques' ? '100%' : '50%'),
                    minHeight: 0,
                    overflow: 'hidden',
                    opacity: expandedPanel === 'terminal' ? 0 : 1,
                    transition: 'width 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease',
                    position: 'relative',
                  }}>
                    <TechniquePanel />
                    {/* Chat overlay — slides down over techniques when active */}
                    {showChatDrawer && (
                      <Box sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        background: 'rgba(15, 23, 42, 0.97)',
                        backdropFilter: 'blur(10px)',
                        borderRadius: 'inherit',
                        zIndex: 15,
                        animation: 'slideDown 0.3s ease-out',
                        '@keyframes slideDown': {
                          from: { opacity: 0, transform: 'translateY(-12px)' },
                          to: { opacity: 1, transform: 'translateY(0)' },
                        },
                      }}>
                        {/* Chat Header */}
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          px: 1.5,
                          py: 0.75,
                          borderBottom: '1px solid rgba(79, 70, 229, 0.12)',
                          background: 'rgba(79, 70, 229, 0.04)',
                          minHeight: 36,
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <ChatIcon sx={{ fontSize: 14, color: '#818cf8' }} />
                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#e2e8f0' }}>
                              Interact with Flow
                            </Typography>
                            {isFlowActive && (
                              <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: '#2dd4bf', animation: 'pulse-dot 2s infinite' }} />
                            )}
                          </Box>
                          <IconButton
                            size="small"
                            onClick={() => setChatOpen(false)}
                            sx={{ color: 'rgba(255, 255, 255, 0.4)', p: 0.25, '&:hover': { color: '#e2e8f0' } }}
                          >
                            <CloseIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Box>

                        {/* Messages */}
                        <Box sx={{
                          flex: 1,
                          overflow: 'auto',
                          px: 1.5,
                          py: 1,
                          '&::-webkit-scrollbar': { width: '4px' },
                          '&::-webkit-scrollbar-track': { background: 'rgba(255,255,255,0.02)' },
                          '&::-webkit-scrollbar-thumb': { background: 'rgba(79, 70, 229, 0.25)', borderRadius: '2px' },
                        }}>
                          {messages.length === 0 && (
                            <Typography sx={{
                              fontSize: '0.7rem',
                              color: 'rgba(255,255,255,0.25)',
                              textAlign: 'center',
                              mt: 4,
                              lineHeight: 1.5,
                            }}>
                              {isFlowActive
                                ? 'Send messages to nudge or redirect the running autonomous flow.'
                                : 'Start an engagement to interact with the flow.'}
                            </Typography>
                          )}
                          {messages.map((message, index) => (
                            <ChatMessage
                              key={index}
                              message={message.text}
                              isUser={message.isUser}
                              isLoading={message.isLoading}
                            />
                          ))}
                          <div ref={messagesEndRef} />
                        </Box>

                        {/* Input */}
                        <Box
                          component="form"
                          onSubmit={handleSubmit}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            px: 1,
                            py: 0.75,
                            borderTop: '1px solid rgba(79, 70, 229, 0.12)',
                          }}
                        >
                          <TextField
                            fullWidth
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder={isFlowActive ? "Send instructions to the flow..." : "No active flow"}
                            disabled={isLoading || !isFlowActive}
                            size="small"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                bgcolor: 'rgba(30, 41, 59, 0.6)',
                                color: '#e2e8f0',
                                fontSize: '0.8rem',
                                '& fieldset': { borderColor: 'rgba(79, 70, 229, 0.15)' },
                                '&:hover fieldset': { borderColor: 'rgba(79, 70, 229, 0.35)' },
                                '&.Mui-focused fieldset': { borderColor: '#6366f1' },
                              },
                              '& .MuiOutlinedInput-input': { py: 0.75 },
                            }}
                          />
                          <IconButton
                            type="submit"
                            disabled={isLoading || !input.trim() || !isFlowActive}
                            size="small"
                            sx={{
                            color: '#818cf8',
                            p: 0.5,
                            '&.Mui-disabled': { color: 'rgba(148,163,184,0.4)' },
                          }}
                          >
                            <SendIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Box>
                      </Box>
                    )}
                  </Box>

                  {/* Vertical Toggle Bar */}
                  <Box sx={{
                    width: '26px',
                    minWidth: '26px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.4, /* 40% smaller total: 0.6 * 0.8 = 0.48 ≈ 0.4 */
                    background: 'linear-gradient(180deg, rgba(15, 23, 42, 0.8) 0%, rgba(30, 41, 59, 0.8) 100%)',
                    border: '1px solid rgba(71, 85, 105, 0.3)',
                    borderTop: 'none',
                    borderBottom: 'none',
                    position: 'relative',
                    zIndex: 10,
                  }}>
                    {/* Expand Techniques Left (◀) */}
                    <Box 
                      onClick={() => setExpandedPanel(expandedPanel === 'techniques' ? null : 'techniques')}
                      sx={{ 
                        cursor: 'pointer',
                        background: expandedPanel === 'techniques' ? 'rgba(79, 70, 229, 0.3)' : 'rgba(79, 70, 229, 0.1)',
                        border: '1px solid rgba(79, 70, 229, 0.3)',
                        borderRadius: '4px', /* 40% smaller total: 5px * 0.8 = 4px */
                        padding: '8px 5px', /* 40% smaller total: 10px * 0.8 = 8px, 6px * 0.8 = 4.8px ≈ 5px */
                        color: '#e2e8f0',
                        fontSize: '0.65rem', /* 40% smaller total: 0.8rem * 0.8 = 0.64rem ≈ 0.65rem */
                        fontWeight: 700,
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        '&:hover': {
                          background: 'rgba(79, 70, 229, 0.25)',
                          borderColor: 'rgba(79, 70, 229, 0.5)',
                          transform: 'scale(1.1)',
                        }
                      }}
                      title="Expand Techniques"
                    >
                      <svg viewBox="0 0 20 20" fill="currentColor" width="12" height="12">
                        {expandedPanel === 'techniques'
                          ? <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                          : <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                        }
                      </svg>
                    </Box>

                    {/* Divider */}
                    <Box sx={{ 
                      width: '13px', /* 40% smaller total: 16px * 0.8 = 12.8px ≈ 13px */
                      height: '1.3px', /* 40% smaller total: 1.6px * 0.8 = 1.28px ≈ 1.3px */
                      background: 'linear-gradient(90deg, transparent, rgba(79, 70, 229, 0.4), transparent)',
                      borderRadius: '1.3px',
                    }} />

                    {/* Expand Terminal Right (▶) */}
                    <Box 
                      onClick={() => setExpandedPanel(expandedPanel === 'terminal' ? null : 'terminal')}
                      sx={{ 
                        cursor: 'pointer',
                        background: expandedPanel === 'terminal' ? 'rgba(79, 70, 229, 0.3)' : 'rgba(79, 70, 229, 0.1)',
                        border: '1px solid rgba(79, 70, 229, 0.3)',
                        borderRadius: '4px', /* 40% smaller total: 5px * 0.8 = 4px */
                        padding: '8px 5px', /* 40% smaller total: 10px * 0.8 = 8px, 6px * 0.8 = 4.8px ≈ 5px */
                        color: '#e2e8f0',
                        fontSize: '0.65rem', /* 40% smaller total: 0.8rem * 0.8 = 0.64rem ≈ 0.65rem */
                        fontWeight: 700,
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        '&:hover': {
                          background: 'rgba(79, 70, 229, 0.25)',
                          borderColor: 'rgba(79, 70, 229, 0.5)',
                          transform: 'scale(1.1)',
                        }
                      }}
                      title="Expand Terminal"
                    >
                      <svg viewBox="0 0 20 20" fill="currentColor" width="12" height="12">
                        {expandedPanel === 'terminal'
                          ? <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                          : <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        }
                      </svg>
                    </Box>
                  </Box>

                  {/* Terminal Output Panel */}
                  <Box sx={{
                    width: expandedPanel === 'techniques' ? '0%' : (expandedPanel === 'terminal' ? '100%' : '50%'),
                    minHeight: 0,
                    overflow: 'hidden',
                    opacity: expandedPanel === 'techniques' ? 0 : 1,
                    transition: 'width 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease',
                  }}>
                    <TerminalOutput />
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </Box>

        {/* Old Dialog removed — chat is now the inline overlay panel in the techniques area */}
      </div>
    </div>
  );
};

export default Offensive;
